import React, { useState } from 'react'

import { CARE_LOCALES, domainLocales } from '@hhgtech/hhg-components/constants'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, Flex, Stack, rem, useMantineTheme } from '@mantine/core'
import { InfoWindow, Marker } from '@react-google-maps/api'
import { useIntl } from 'react-intl'

import { getCareHospitalUrl } from 'api/utils/paths'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { CARE_LOCALISED_SLUGS } from 'interfaces/constants'
import { CareHospital } from 'interfaces/types'

export function SelectedClinicMarker({
  selectedClinic,
  hideBooking,
}: {
  selectedClinic: CareHospital
  hideBooking?: boolean
}) {
  const { formatMessage: f } = useIntl()
  const theme = useMantineTheme()
  const [
    selectedMarker,
    setSelectedMarker,
  ] = useState<google.maps.Marker | null>()
  const { locale } = useRouterExtend()
  const homepageUrl = CARE_LOCALES.includes(locale)
    ? getCareHospitalUrl({
        id: selectedClinic.id,
        name: selectedClinic.name,
        locale,
        slug: selectedClinic.slug,
      })
    : null
  const btnProps =
    selectedClinic.totalAvailableDoctors &&
    selectedClinic.totalAvailableServices
      ? {
          label: f({ id: 'articleAuthor.button', defaultMessage: 'Book now' }),
          url: homepageUrl,
        }
      : selectedClinic.totalAvailableDoctors
      ? {
          label: f({ id: 'articleAuthor.button', defaultMessage: 'Book now' }),
          url: `${homepageUrl}?tab=${CARE_LOCALISED_SLUGS[locale]?.DOCTOR}`,
        }
      : selectedClinic.totalAvailableServices
      ? {
          label: f({ id: 'articleAuthor.button', defaultMessage: 'Book now' }),
          url: `${homepageUrl}?tab=${CARE_LOCALISED_SLUGS[locale]?.SERVICE}`,
        }
      : {
          label: f({ id: 'articlePage.careEmbedding.viewDetail' }),
          url: homepageUrl,
        }
  return (
    <>
      <Marker
        position={{
          lat: Number(selectedClinic.latitude),
          lng: Number(selectedClinic.longitude),
        }}
        // icon={{
        //   url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
        // }}
        onLoad={setSelectedMarker}
      />
      {selectedMarker && (
        <InfoWindow
          // onLoad={onLoad}
          // position={{
          //   lat: Number(selectedClinic.latitude),
          //   lng: Number(selectedClinic.longitude),
          // }}
          anchor={selectedMarker}
          key={selectedClinic.id}
          options={{
            minWidth: 320,
            maxWidth: 320,
          }}
        >
          <Stack spacing={rem(4)}>
            {selectedClinic.logo ? (
              <div>
                <img
                  loading="lazy"
                  src={selectedClinic.logo}
                  alt={selectedClinic.name}
                  width={'auto'}
                  height={48}
                />
              </div>
            ) : null}
            <Stack spacing={rem(4)}>
              <Text size="s4" weight="semiBold">
                {selectedClinic.name}
              </Text>
              <Text size="n1">{selectedClinic.address}</Text>
              {!hideBooking &&
              (selectedClinic.totalAvailableDoctors ||
                selectedClinic.totalAvailableServices) ? (
                <Text size="n1">
                  {f({
                    id: 'clinicLocator.infoWindow.availableForBooking',
                    defaultMessage: 'Available for booking',
                  })}
                  {': '}
                  <Box
                    component="span"
                    sx={(theme) => ({
                      span: {
                        display: 'inline-block',
                      },
                      'span + span': {
                        position: 'relative',
                        marginLeft: rem(5),
                        paddingLeft: rem(5),
                        '&:before': {
                          content: '""',
                          width: rem(2),
                          height: rem(2),
                          borderRadius: '50%',
                          background: theme.colors.gray[6],
                          position: 'absolute',
                          left: '0',
                          top: '50%',
                        },
                      },
                    })}
                  >
                    {selectedClinic.totalAvailableDoctors > 0 && (
                      <span>
                        {selectedClinic.totalAvailableDoctors
                          ? f({
                              id: 'clinicLocator.infoWindow.availableDoctors',
                            })
                          : ''}
                      </span>
                    )}
                    {selectedClinic.totalAvailableServices > 0 && (
                      <span>
                        <span>
                          {selectedClinic.totalAvailableServices
                            ? f({
                                id:
                                  'clinicLocator.infoWindow.availableServices',
                              })
                            : ''}
                        </span>
                      </span>
                    )}
                  </Box>
                </Text>
              ) : null}
              {((!hideBooking && btnProps.url) ||
                selectedClinic.websiteUrl) && (
                <Flex
                  align={'center'}
                  gap={16}
                  sx={{
                    'a:focus-visible': {
                      outline: 'none',
                    },
                  }}
                >
                  {!hideBooking && btnProps.url && (
                    <Box
                      component="a"
                      href={btnProps.url}
                      target="_blank"
                      className="pointer-event-child-none"
                      data-event-category="Service Connection"
                      data-event-action="Book Doctor via Clinic Click"
                      data-event-label={`https://${domainLocales[locale]}${btnProps.url}`}
                    >
                      <Text
                        size="n1"
                        sx={{
                          color: theme.colors.blue[8],
                          textDecoration: 'underline',
                          textUnderlineOffset: rem(4),
                        }}
                      >
                        {btnProps.label}
                      </Text>
                    </Box>
                  )}
                  {selectedClinic.websiteUrl && (
                    <Box
                      component="a"
                      href={selectedClinic.websiteUrl}
                      target="_blank"
                      className="pointer-event-child-none"
                      data-event-category="Service Connection"
                      data-event-action="Clinic Directory - Go to Hospital Page"
                      data-event-label={selectedClinic.websiteUrl}
                    >
                      <Text
                        size="n1"
                        sx={{
                          color: theme.colors.blue[8],
                          textDecoration: 'underline',
                          textUnderlineOffset: rem(4),
                        }}
                      >
                        {f({
                          id: 'clinicLocator.infoWindow.websiteUrl',
                          defaultMessage: 'Go to hospital page',
                        })}
                      </Text>
                    </Box>
                  )}
                </Flex>
              )}
            </Stack>
          </Stack>
        </InfoWindow>
      )}
    </>
  )
}
